import React, { useState } from "react";
import { SubscriptionPlan } from "./subscriptionData";
import Label from "../Common/Label";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const VISIBLE_FEATURES = 4;

interface SubscriptionCardProps {
  plan: SubscriptionPlan;
  onPlanSelect?: (planId: string) => void;
  isLoading: boolean;
}

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  plan,
  onPlanSelect,
  isLoading,
}) => {
  const [expanded, setExpanded] = useState(false);
  const featureCount = plan.features?.length || 0;
  const hasMoreFeatures = featureCount > VISIBLE_FEATURES;
  const visibleFeatures = expanded
    ? plan.features
    : plan.features?.slice(0, VISIBLE_FEATURES);

  const cardClasses = `subscription-card ${
    plan.isPopular ? "popular-card" : ""
  }`;

  const handleButtonClick = () => {
    if (onPlanSelect) {
      onPlanSelect(plan.subscriptionPlanId);
    }
  };

  const renderPrice = () => {
    if (plan.price === 0) {
      return <span className="price-free">7 Days Free</span>;
    }

    return (
      <>
        <span className="price-currency">€</span>
        <span className="price-amount">{plan.price}</span>
        <span className="price-period">{plan.period}</span>
      </>
    );
  };

  return (
    <div className={cardClasses}>
      {plan.badge && (
        <div className="badge-wrapper">
          <Label
            children={plan.badge}
            className="value-badge"
            fontWeight={700}
            fontSize={"11px"}
            color="#244565"
          />
        </div>
      )}

      <div className="card-header">
        <Label
          children={plan.name}
          className="plan-name"
          fontWeight={700}
          fontSize={"22px"}
        />
        <div className="price-section">{renderPrice()}</div>
        <span className="feature-count-badge">
          {featureCount} features included
        </span>
      </div>

      <div className="card-footer">
        <button
          className={`btn-subscription ${plan.isPopular ? "btn-popular" : ""}`}
          onClick={handleButtonClick}
          type="button"
          disabled={isLoading}
          aria-label={`Select ${plan.name} plan for €${plan.price}${plan.period}`}
        >
          {isLoading ? (
            "Processing..."
          ) : (
            <>
              {plan.buttonText}
              <ArrowForwardIcon
                sx={{
                  fontSize: 16,
                  ml: 1,
                  verticalAlign: "middle",
                  mb: "2px",
                }}
              />
            </>
          )}
        </button>
      </div>

      <div
        className={`features-list ${expanded ? "features-list--expanded" : ""}`}
      >
        {visibleFeatures?.map((feature, index) => (
          <div key={index} className="feature-item">
            <span className="feature-check-icon">
              <CheckCircleOutlineIcon sx={{ fontSize: 13 }} />
            </span>
            <span className="feature-text">{feature.name}</span>
          </div>
        ))}
      </div>

      {hasMoreFeatures && (
        <button
          type="button"
          className="features-toggle"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
        >
          {expanded ? (
            <>
              Show less
              <ExpandLessIcon sx={{ fontSize: 16 }} />
            </>
          ) : (
            <>
              View all {featureCount} features
              <ExpandMoreIcon sx={{ fontSize: 16 }} />
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default SubscriptionCard;
